import {
	DefaultButton,
	IDetailsList,
	IPanelProps,
	Panel,
	PanelType,
	PrimaryButton,
	Separator,
	Spinner,
	Stack,
	Toggle,
} from '@fluentui/react';
import {
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListFilter,
} from './InfinityList';
import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useBoolean} from '@fluentui/react-hooks';
import {FilterItem, FilterItemDate} from './InfinityListFilterItem';
import {useCommand} from 'hooks';
import {v4 as uuidv4} from 'uuid';
import InfinityListAdvancedFilterTree, {
	AdvancedFilterTreeNode,
	determineDefaultAdvancedFilterTree,
} from './InfinityListAdvancedFilter';

export type InfinityListFilterPanelProps = {
	sortField: string;
	sortOrder: string;
	currentView: string;
	isPanelOpened: boolean;
	stillLoading: boolean;
	advancedFilterShow?: boolean;
	columns: InfinityListColumn[];
	columnFilterOptions?: InfinityListColumnFilterOptions[];
	allListFilters: InfinityListFilter[];
	appliedFilters: InfinityListFilter[];
	listRef: React.MutableRefObject<IDetailsList | undefined>;
	openPanel: () => void;
	dismissPanel: () => void;
	setAllListFilters: (allListFilters: InfinityListFilter[]) => void;
	setAppliedFilters: (appliedFilters: InfinityListFilter[]) => void;
	applyFilter: (
		filters: InfinityListFilter[],
		sortField: string,
		sortOrder: string,
		currentView?: string,
		preFilteredIds?: string[],
		advancedFilterOn?: boolean,
		advancedFilterNodeList?: AdvancedFilterTreeNode[],
	) => void;
};

export const InfinityListFilterPanel: React.FC<
	InfinityListFilterPanelProps
> = ({
	sortField,
	sortOrder,
	currentView,
	isPanelOpened,
	stillLoading,
	advancedFilterShow,
	columns,
	columnFilterOptions,
	allListFilters,
	appliedFilters,
	listRef,
	openPanel,
	dismissPanel,
	setAllListFilters,
	setAppliedFilters,
	applyFilter,
}) => {
	const {t} = useTranslation('components/infinitylist');

	const [advancedFilterOn, {toggle: toggleAdvancedFilter}] = useBoolean(false);
	const [advancedFilterNodeList, setAdvancedFilterNodeList] = useState<
		AdvancedFilterTreeNode[]
	>([]);

	const arrFieldValuesUnique = useMemo(
		() =>
			columns
				.filter(col => col.filterType === 'Array')
				.map(
					col =>
						(columnFilterOptions ?? []).find(x => x.key === col.key)
							?.fieldValuesUnique,
				),
		[columns, columnFilterOptions],
	);

	const filterContent = useMemo(
		() => (
			<>
				{columns
					.filter(col => col.filterType === 'Array')
					.map((col, i) => {
						return (
							<FilterItem
								key={col.key}
								col={col}
								fieldValuesUnique={arrFieldValuesUnique.at(i)}
								allListFilters={allListFilters}
								setAllListFilters={setAllListFilters}
							/>
						);
					})}
				{columns
					.filter(col => col.filterType === 'Date')
					.map((col, i) => {
						return (
							<FilterItemDate
								key={col.key}
								col={col}
								allListFilters={allListFilters}
								setAllListFilters={setAllListFilters}
							/>
						);
					})}
			</>
		),

		[allListFilters, columns, columnFilterOptions],
	);

	const handleApplyFilters = () => {
		applyFilter(
			allListFilters,
			sortField,
			sortOrder,
			currentView,
			undefined,
			advancedFilterOn,
			advancedFilterNodeList,
		);
		setAppliedFilters([...allListFilters]);
	};

	const handleRemoveFilters = () => {
		listRef.current?.scrollToIndex(0);
		setAllListFilters([]);
		setAppliedFilters([]);
		applyFilter([], sortField, sortOrder, currentView);
	};

	const handleClickFilterToggle = () => {
		toggleAdvancedFilter();
		setAllListFilters(appliedFilters);
		if (!advancedFilterOn) {
			const nodeListNew = determineDefaultAdvancedFilterTree(
				allListFilters,
				columns,
			);
			setAdvancedFilterNodeList(nodeListNew);
		}
	};

	const onRenderFooterContent = React.useCallback(
		() => (
			<>
				<Stack horizontal tokens={{childrenGap: 10}}>
					<PrimaryButton onClick={handleApplyFilters}>
						{stillLoading ? <Spinner /> : t('ApplyFilters')}
					</PrimaryButton>
					<DefaultButton
						text={t('ClearFilters')}
						onClick={handleRemoveFilters}
					/>
				</Stack>
				{advancedFilterShow !== false && (
					<Toggle
						label={t('AdvancedFilterToggle')}
						checked={advancedFilterOn}
						onChange={handleClickFilterToggle}
					/>
				)}
			</>
		),
		[
			allListFilters,
			applyFilter,
			advancedFilterOn,
			advancedFilterNodeList,
			currentView,
			stillLoading,
		],
	);

	const onRenderBody: IPanelProps['onRenderBody'] = props => {
		return (
			<div
				style={{
					flexGrow: 1,
					overflowY: 'auto',
					padding: '10px 20px 10px 20px',
				}}
			>
				{!advancedFilterOn && filterContent}
				{advancedFilterOn && (
					<InfinityListAdvancedFilterTree
						columns={columns}
						columnFilterOptions={columnFilterOptions}
						advancedFilterNodeList={advancedFilterNodeList}
						setAdvancedFilterNodeList={setAdvancedFilterNodeList}
					/>
				)}
			</div>
		);
	};

	useCommand(
		{
			key: uuidv4(),
			priority: 1,
			iconProps: {
				iconName: 'Filter',
			},
			farCommand: true,
			onClick() {
				listRef.current?.scrollToIndex(0);
				openPanel();
			},
			ariaLabel: 'Filter',
			title: 'Filter',
		},
		[listRef.current, appliedFilters, allListFilters],
	);

	useCommand(
		{
			key: 'clearFilter',
			farCommand: true,
			hidden: appliedFilters.length === 0,
			iconProps: {
				iconName: 'ClearFilter',
			},
			onClick() {
				listRef.current?.scrollToIndex(0);
				setAllListFilters([]);
				setAppliedFilters([]);
				applyFilter([], sortField, sortOrder, currentView);
			},
		},
		[
			listRef.current,
			applyFilter,
			allListFilters,
			appliedFilters,
			sortField,
			sortOrder,
			currentView,
		],
	);

	const handleDismissFilterPanel = () => {
		dismissPanel();
	};

	return (
		<Panel
			type={PanelType.medium}
			isLightDismiss
			isOpen={isPanelOpened}
			closeButtonAriaLabel='Close'
			onDismiss={handleDismissFilterPanel}
			onRenderFooterContent={onRenderFooterContent}
			onRenderBody={onRenderBody}
			headerText='Filter'
			isFooterAtBottom={true}
		></Panel>
	);
};
